import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {useLocation} from 'react-router-dom';

import {always} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';
import {DmsDataGrid, Main, PageTab} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {rentalOrderBulkOperationActionCallback} from '../../utils/rentalOrderBulkOperationActionCallback';
import {RentalCalendarBlockDialog} from './(Sections)/RentalOrderCalendar/components/RentalCalendarBlockDialog';
import {useCanCreateVehicleBlock} from './(Sections)/RentalOrderCalendar/hooks/useCanCreateVehicleBlock';
import {RentalOrderCalendar} from './(Sections)/RentalOrderCalendar/RentalOrderCalendar';
import {RentalOrderKanban} from './(Sections)/RentalOrderKanban/RentalOrderKanban';

const PREDEFINED_COLUMNS = ['state'] as const;

export function RentalOrderOverview() {
  const navigate = useNavigate();
  const location = useLocation();

  const canCreateVehicleBlock = useCanCreateVehicleBlock();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    sourceSearchParams,
    getColumnData,
  }) => {
    if (isArray(rowId)) {
      return;
    }

    match(actionKey)
      .with('detail', 'detail_newTab', () => {
        const state = getColumnData<{key: RentalOrderResponseBody['state']}>('state')?.value?.key;

        const redirectRoute = match(state)
          .with('draft', 'canceled', 'closed', always(rentalOrderRoutes.vehicleAndCustomer))
          .with('reserved', always(rentalOrderRoutes.rentVehicle))
          .with('rented', 'returned', always(rentalOrderRoutes.returnVehicle))
          .otherwise(always(rentalOrderRoutes.vehicleAndCustomer));

        const queryParams = new URLSearchParams(sourceSearchParams);
        queryParams.set('source', 'rental-order-detail');

        const url = composePath(redirectRoute, {
          params: {id: rowId},
          isSearchParamsPreserved: false,
          queryParams: queryParams.toString(),
        });

        if (actionKey === 'detail_newTab') {
          window.open(url, '_blank');
        } else {
          navigate(url);
        }
      })
      .otherwise(noop);
  };

  const onAddVehicleBlock = () =>
    openDialog(
      <RentalCalendarBlockDialog data-testid={testIds.rentalOrder.overview('vehicleBlock')} />,
      {
        title: i18n.t('entity.rental.actions.addVehicleBlock'),
        size: 'default',
        'data-testid': testIds.rentalOrder.overview('blockDialog'),
      }
    );

  const isCalendarTabActive = location.pathname === rentalOrderRoutes.overviewCalendar;

  const addVehicleBlockAction: Action = {
    type: 'button',
    onClick: onAddVehicleBlock,
    variant: 'secondary',
    leftIcon: 'content/add_circle',
    'data-testid': testIds.rentalOrder.overview('addVehicleBlock'),
    title: i18n.t('entity.rental.actions.addVehicleBlock'),
  };

  const actions: Action[] = [
    ...(isCalendarTabActive && canCreateVehicleBlock ? [addVehicleBlockAction] : []),
    {
      type: 'button',
      onClick: () => navigate(rentalOrderRoutes.create),
      variant: 'primary',
      'data-testid': testIds.rentalOrder.overview('create'),
      title: i18n.t('entity.rentalOrder.actions.newRentalOrder'),
    },
  ];

  const tabs: PageTab[] = [
    {
      content: (
        <DmsDataGrid
          data-testid={testIds.rentalOrder.overview('datagrid')}
          gridCode="rental-order"
          actionCallback={actionCallback}
          bulkOperationActionCallback={rentalOrderBulkOperationActionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      ),
      href: rentalOrderRoutes.overview,
      id: 'overview',
      title: i18n.t('page.rentalOrder.labels.overview'),
    },
    {
      content: <RentalOrderKanban />,
      href: rentalOrderRoutes.overviewKanban,
      id: 'overview-kanban',
      title: i18n.t('page.rentalOrder.labels.overviewKanban'),
    },
    {
      content: <RentalOrderCalendar />,
      href: rentalOrderRoutes.overviewCalendar,
      id: 'overview-calendar',
      title: i18n.t('page.rentalOrder.labels.overviewCalendar'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('page.rentalOrder.labels.overview')} />
      <Main
        isFullHeight
        actions={actions}
        data-testid={testIds.rentalOrder.overview('container')}
        tabs={tabs}
      />
    </>
  );
}
